import type { LeaderboardItemPropTypes } from './List.types';
import type { ReactElement } from 'react';

import { Badge, Box, Card, Flex, Text, Tooltip } from '@rango-dev/whale';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../hooks/reduxHooks';
import { profileSummarySelector } from '../../../../state/ProfileSlice/selectors';
import { Avatar } from '../../Avatar';
import { displayUserRank } from '../../ProfileSummary/ProfileSummary.helpers';
import { shortenUsername } from '../Leaderboard.helpers';

import styles from './List.module.css';

const TOP_USER_RANK_THRESHOLD = 3;

export function Item(props: LeaderboardItemPropTypes): ReactElement {
  const {
    userId,
    item,
    className,
    showCurrentUserTag,
    showLevel,
    isCompactUsername,
    formatScore,
    scoreIcon,
    enableTopUserStyles = false,
    avatarSize = '3',
  } = props;
  const isCurrentUser = userId === item.id;
  const params = useParams<{ username: string }>();
  const { data: profileSummary } = useAppSelector((state) =>
    profileSummarySelector(state, params.username),
  );
  const idMatched = item.id === profileSummary?.id && !!profileSummary?.username;
  const username = idMatched ? profileSummary.username : item.username;
  const { t } = useTranslation();

  const showTopUserStyles =
    item.rank > 0 && item.rank <= TOP_USER_RANK_THRESHOLD && enableTopUserStyles;

  const rankNode = showTopUserStyles ? (
    <Box className={`${styles.topRanks} ${styles[`topRank${item.rank}`]}`}>
      <img className={styles.topRankImage} src={`/imgs/leaderboard/rank${item.rank}.svg`} alt="" />
      <Box className={styles.topRankBorder} />
      <Box className={styles.topRankGradient} />
    </Box>
  ) : (
    <Badge
      variant="soft"
      color="gray"
      size="1"
      radius="full"
      mr={{ initial: '2', sm: '4' }}
      className={`${styles.rankBadge} ${isCurrentUser ? styles.currentUserRank : styles.userRank}`}>
      {displayUserRank(item.rank)}
    </Badge>
  );

  const itemClassName = [className ?? '', isCurrentUser ? styles.currentUserItem : '', styles.item]
    .filter(Boolean)
    .join(' ');

  return (
    <Card
      size={{ initial: '1', sm: '2' }}
      variant={isCurrentUser ? 'surface' : 'ghost'}
      className={`${itemClassName} ${styles.itemCard}`}>
      <Link id="leader-board-item-link" to={`../profile/${username}`} className={styles.itemLink}>
        <Flex className="user-item-row" justify="between" align="center">
          <Flex align="center">
            {rankNode}
            <Box className="user-image-content" position="relative" mr={{ initial: '2', sm: '4' }}>
              <Avatar username={username} size={avatarSize} />
              {showLevel && (
                <Badge
                  color="teal"
                  variant="solid"
                  radius="large"
                  size="1"
                  className={styles.levelBadge}>
                  {item.level}
                </Badge>
              )}
            </Box>
            <Flex
              width={{ initial: '90px', sm: 'auto' }}
              direction={{ initial: 'column', md: 'row' }}
              align={{ initial: 'start', md: 'center' }}
              mr={{ initial: '2', sm: '4' }}
              gap="2">
              <Text
                className={`leaderboard-username ${styles.leaderboardUsername}`}
                size={{ initial: '2', md: '3' }}>
                {isCompactUsername ? shortenUsername({ username }) : username}
              </Text>
              {showCurrentUserTag && isCurrentUser && (
                <Badge variant="surface" color="cyan" size="2">
                  {t('You')}
                </Badge>
              )}
            </Flex>
          </Flex>
          <Flex align="center" gap="1" className="score-section">
            {scoreIcon}
            <Tooltip content={item.score.toString()}>
              <Text size={{ initial: '2', sm: '3' }} weight="medium">
                {!formatScore ? displayUserRank(item.score) : formatScore(item)}
              </Text>
            </Tooltip>
          </Flex>
        </Flex>
      </Link>
    </Card>
  );
}
